import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Avatar,
  Grid,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  Stack,
  Container,
  useTheme
} from '@mui/material';
import { styled } from '@mui/system';
import SecurityIcon from '@mui/icons-material/Security';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import { getSubscriptionDetails, redirectToCheckout, cancelStripeSubscription, redirectToCustomerPortal, SUBSCRIPTION_TIERS } from '../services/subscriptionService';
import { supabase } from '../supabaseClient';
import { generateRandomName } from '../utils/nameGenerator';

// Brand colors from the design
const brandColors = {
  primary: '#004aad', // Dark blue from the logo
  secondary: '#A2C3A4', // Light green
  accent: '#cbf199', // Light lime green
  success: '#4CAF50',
  warning: '#FFC107',
  error: '#F44336',
  neutral: '#869d96', // Gray-green
  lightBg: '#fdfdfd', // Almost white
  darkBg: '#0F2544', // Darker blue for dark mode
  cardBg: '#FFFFFF',
  cardBgDark: 'rgba(15, 37, 68, 0.8)',
  gradientLight: 'linear-gradient(135deg, #fdfdfd 0%, rgba(203, 241, 153, 0.2) 100%)',
  gradientDark: 'linear-gradient(135deg, rgba(0, 74, 173, 0.9) 0%, rgba(26, 58, 107, 0.8) 100%)',
};

// Common features shared by both plans
const commonFeatures = [
  '2K Render Kalitesi',
  'İç Mekan Düzenleme',
  'Seç ve Değiştir Özelliği',
  'Referans Tasarım Ekleme',
  'Mobilya Yenileme',
  'Türkçe Arayüz',
  'Görsel Düzenleyici',
  'Video Oluşturucu',
  'Blog Oluşturucu',
  'Sanal Dekorasyon',
  'Hızlı Dekorasyon',
  'Yeniden Döşeme',
  'Yeniden Tasarım',
  'Yapay Zeka Asistanları',
  'Sohbet Görsel Düzenleyici',
  'Depolama',
  'Şifre Korumalı Galeri Paylaşımı',
  'Paylaşım Yönetimi',
  'PDF Dışa Aktarma',
  'HTML Blog Dışa Aktarma'
];

// Features exclusive to Pro plan
const proExclusiveFeatures = [
  '7/24 Müşteri Desteği',
  'Şirketler için Özel Filigran'
];

const ProfileAvatar = styled(Avatar)(({ theme }) => ({
  width: 100,
  height: 100,
  margin: '0 auto',
  border: `3px solid ${theme.palette.mode === 'dark' ? 'rgba(74, 144, 226, 0.3)' : 'rgba(74, 144, 226, 0.2)'}`,
  boxShadow: theme.palette.mode === 'dark'
    ? `0 0 20px rgba(74, 144, 226, 0.2)`
    : '0 0 20px rgba(0, 0, 0, 0.05)',
  transition: 'all 0.3s ease-in-out',
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(26, 58, 107, 0.5)' : '#F5F8FC',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: theme.palette.mode === 'dark'
      ? `0 0 25px rgba(74, 144, 226, 0.3)`
      : '0 0 25px rgba(74, 144, 226, 0.2)',
  }
}));

const FeatureList = styled('ul')(({ theme }) => ({
  listStyle: 'none',
  padding: 0,
  margin: '16px 0',
  '& li': {
    margin: '10px 0',
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.9rem',
    color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.75)',
  },
}));

const FeatureIcon = styled(CheckCircleIcon)(({ theme }) => ({
  marginRight: '10px',
  fontSize: '1rem',
  color: theme.palette.mode === 'dark' ? brandColors.accent : brandColors.primary,
}));

const PlanCard = styled(Card)(({ theme, selected }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(15, 37, 68, 0.6)' : '#ffffff',
  borderRadius: '16px',
  border: selected 
    ? `2px solid ${theme.palette.mode === 'dark' ? brandColors.accent : brandColors.primary}` 
    : `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.06)'}`,
  transition: 'all 0.3s ease-in-out',
  position: 'relative',
  overflow: 'visible',
  height: '100%',
  backdropFilter: 'blur(10px)',
  boxShadow: selected
    ? theme.palette.mode === 'dark'
      ? '0 8px 32px rgba(74, 144, 226, 0.3)'
      : '0 8px 32px rgba(26, 58, 107, 0.15)'
    : 'none',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.palette.mode === 'dark'
      ? `0 12px 28px rgba(74, 144, 226, 0.25)`
      : '0 12px 28px rgba(26, 58, 107, 0.12)',
  },
}));

const PlanBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '-10px',
  right: '20px',
  background: theme.palette.mode === 'dark'
    ? 'linear-gradient(135deg, #4A90E2 0%, #1A3A6B 100%)'
    : 'linear-gradient(135deg, #1A3A6B 0%, #4A90E2 100%)',
  color: 'white',
  padding: '4px 12px',
  borderRadius: '20px',
  fontSize: '0.7rem',
  fontWeight: 'bold',
  letterSpacing: '0.5px',
  boxShadow: theme.palette.mode === 'dark'
    ? '0 4px 12px rgba(74, 144, 226, 0.3)'
    : '0 4px 12px rgba(26, 58, 107, 0.2)',
  zIndex: 1,
}));

const StripeBadge = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  padding: '10px 16px',
  borderRadius: '10px',
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
  marginTop: '16px',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)'}`,
  '& img': {
    height: '18px',
  }
}));

const SecurityBadge = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  padding: '12px',
  borderRadius: '12px',
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(15, 37, 68, 0.4)' : 'rgba(245, 248, 252, 0.8)',
  marginTop: '24px',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(74, 144, 226, 0.15)' : 'rgba(26, 58, 107, 0.08)'}`,
  backdropFilter: 'blur(8px)',
  boxShadow: theme.palette.mode === 'dark' 
    ? 'none' 
    : '0 4px 12px rgba(0, 0, 0, 0.03)',
}));

const PageHeader = styled(Box)(({ theme }) => ({
  background: theme.palette.mode === 'dark' 
    ? `linear-gradient(135deg, rgba(0, 74, 173, 0.8) 0%, rgba(26, 58, 107, 0.8) 100%)`
    : `linear-gradient(135deg, rgba(253, 253, 253, 0.9) 0%, rgba(203, 241, 153, 0.2) 100%)`,
  padding: '28px 0',
  borderRadius: '16px',
  marginBottom: '32px',
  position: 'relative',
  overflow: 'hidden',
  backdropFilter: 'blur(10px)',
  boxShadow: theme.palette.mode === 'dark'
    ? '0 8px 24px rgba(0, 0, 0, 0.15)'
    : '0 4px 16px rgba(0, 0, 0, 0.03)',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(0, 74, 173, 0.1)' : 'rgba(162, 195, 164, 0.2)'}`,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: theme.palette.mode === 'dark'
      ? 'radial-gradient(circle at 20% 150%, rgba(203, 241, 153, 0.1) 0%, transparent 60%), radial-gradient(circle at 80% 50%, rgba(0, 74, 173, 0.15) 0%, transparent 50%)'
      : 'radial-gradient(circle at 20% 150%, rgba(203, 241, 153, 0.3) 0%, transparent 60%), radial-gradient(circle at 80% 50%, rgba(162, 195, 164, 0.1) 0%, transparent 50%)',
    zIndex: 0,
  }
}));

const SubscriptionCard = styled(Card)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(15, 37, 68, 0.6)' : 'rgba(255, 255, 255, 0.9)',
  borderRadius: '16px',
  transition: 'all 0.3s ease-in-out',
  overflow: 'hidden',
  backdropFilter: 'blur(10px)',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(74, 144, 226, 0.1)' : 'rgba(26, 58, 107, 0.06)'}`,
  boxShadow: theme.palette.mode === 'dark'
    ? '0 4px 20px rgba(0, 0, 0, 0.2)'
    : '0 4px 20px rgba(0, 0, 0, 0.03)',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.palette.mode === 'dark'
      ? `0 8px 28px rgba(74, 144, 226, 0.15)`
      : '0 8px 28px rgba(26, 58, 107, 0.08)',
  },
}));

const Subscription = ({ session }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [profileData, setProfileData] = useState({ name: '', avatarUrl: '' });
  const [checkoutLoading, setCheckoutLoading] = useState(null); // Track loading state per tier

  // Fetch profile and subscription data
  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      if (!session?.user?.id) {
        setLoading(false);
        // Redirect if not logged in (handled by parent or router guard ideally)
        // navigate('/login');
        return;
      }
      setLoading(true);
      setError('');
      try {
        const [details, { data: profile, error: profileError }] = await Promise.all([
          getSubscriptionDetails(session.user.id),
          supabase.from('profiles').select('name, avatar_url').eq('id', session.user.id).single()
        ]);

        if (!isMounted) return;

        if (profileError && profileError.code !== 'PGRST116') throw profileError;

        setSubscriptionDetails(details);

        let currentName = profile?.name;
        // Generate and save name only if it's truly missing or empty
        if (!currentName) {
            currentName = generateRandomName();
            // Use await to ensure the upsert completes before setting state if needed,
            // though setting state immediately is usually fine for UI.
            await supabase.from('profiles').upsert({ id: session.user.id, name: currentName });
        }

        setProfileData({
          name: currentName,
          avatarUrl: profile?.avatar_url || ''
        });

      } catch (err) {
        if (isMounted) {
          console.error('Error fetching data:', err);
          setError('Profil ve abonelik verileri yüklenemedi.');
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchData();
    return () => { isMounted = false; };
  // Add profileData.name to dependency array if upsert depends on it, otherwise session is enough
  }, [session]);

  // Handle Cancellation
  const handleCancel = async () => {
    if (!session?.user?.id) return;
    if (window.confirm('Aboneliğinizi iptal etmek istediğinizden emin misiniz? Dönem sonunda erişiminizi kaybedeceksiniz.')) {
      setCancelling(true);
      setError('');
      setSuccess('');
      try {
        await cancelStripeSubscription(session.user.id);
        setSuccess('Abonelik iptal talebiniz alındı. Değişiklikler kısa süre içinde yansıyacaktır.');
        // Refresh data after a delay to allow webhook processing
        setTimeout(() => {
          if (session?.user?.id) { // Check session again inside timeout
             getSubscriptionDetails(session.user.id).then(setSubscriptionDetails);
          }
        }, 6000); // Increased delay slightly
      } catch (err) {
        setError(err.message || 'Abonelik iptal edilemedi.');
      } finally {
        setCancelling(false);
      }
    }
  };

  // Handle Upgrade/Checkout
  const handleUpgrade = async (tier) => {
    if (!session?.user?.id) {
      setError('Lütfen önce giriş yapın.');
      return;
    }
    
    // Hardcoded price IDs as fallback
    const hardcodedPriceIds = {
      'basic': 'price_1R8xjLE6SBER9KtGSDbrBLU5',
      'pro': 'price_1R8xilE6SBER9KtGYklkN61n' // Replace with your actual Pro price ID
    };
    
    // Try to get from env vars first, then fallback to hardcoded
    let priceId = process.env[`REACT_APP_STRIPE_${tier.toUpperCase()}_PRICE_ID`];
    
    // If not found in env vars, use hardcoded
    if (!priceId && hardcodedPriceIds[tier]) {
      console.log(`Using hardcoded price ID for ${tier} tier: ${hardcodedPriceIds[tier]}`);
      priceId = hardcodedPriceIds[tier];
    }
    
    if (!priceId) {
      console.error(`Stripe Price ID not found for tier: ${tier}. Check environment variable REACT_APP_STRIPE_${tier.toUpperCase()}_PRICE_ID`);
      setError(`${tier} planı için yapılandırma hatası. Lütfen destek ile iletişime geçin.`);
      return;
    }
    
    console.log(`Upgrading to ${tier} tier with price ID: ${priceId}`);
    setCheckoutLoading(tier);
    setError('');
    try {
      await redirectToCheckout(priceId, session.user.id);
      // Redirect happens within redirectToCheckout
    } catch (err) {
      setError(err.message || 'Ödeme sayfasına yönlendirilemedi.');
      setCheckoutLoading(null);
    }
    // No finally block needed here as redirect occurs on success
  };

  // Handle Manage Billing
  const handleManageBilling = async () => {
    if (!session?.user?.id) return;
    setLoading(true); // Use general loading state for portal redirect
    setError('');
    try {
      await redirectToCustomerPortal(session.user.id);
      // Redirect happens within redirectToCustomerPortal
    } catch (err) {
      setError(err.message || 'Fatura portalına yönlendirilemedi.');
      setLoading(false); // Set loading false only on error
    }
    // No finally block needed here as redirect occurs on success
  };

  // Show loading indicator only on the initial fetch
  if (loading && !subscriptionDetails) {
    return (
      <Box sx={{ width: '100%', mt: 4 }}>
        <LinearProgress 
          sx={{ 
            height: 6, 
            borderRadius: 3,
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
            '& .MuiLinearProgress-bar': {
              backgroundColor: brandColors.accent
            }
          }} 
        />
      </Box>
    );
  }

  const currentTier = subscriptionDetails?.subscription_tier || 'free';
  const isActive = subscriptionDetails?.subscription_status === 'active' || subscriptionDetails?.subscription_status === 'trialing';
  const isCancelling = subscriptionDetails?.subscription_status === 'cancelling';
  
  // Get plan details for display
  const getPlanDetails = (tier) => {
    const plans = {
      'free': {
        name: 'Ücretsiz Plan',
        price: '0 ₺/ay',
        description: 'Temel özellikler ile başlayın',
        color: theme.palette.mode === 'dark' ? '#555' : '#777',
        isPopular: false,
        buttonText: 'Mevcut Plan',
        buttonVariant: 'outlined',
      },
      'basic': {
        name: 'Temel Paket',
        price: '159 ₺/ay',
        description: 'Bireysel mimarlar ve küçük ofisler için',
        credits: '500 Render Kredisi/Ay',
        color: brandColors.secondary,
        isPopular: true,
        buttonText: currentTier === 'basic' ? 'Mevcut Plan' : 'Yükselt',
        buttonVariant: currentTier === 'basic' ? 'outlined' : 'contained',
      },
      'pro': {
        name: 'Profesyonel',
        price: '599 ₺/ay',
        description: 'Profesyonel mimarlık ofisleri için tam donanımlı çözüm',
        credits: '2000 Render Kredisi/Ay',
        color: brandColors.primary,
        isPopular: false,
        buttonText: currentTier === 'pro' ? 'Mevcut Plan' : 'Yükselt',
        buttonVariant: currentTier === 'pro' ? 'outlined' : 'contained',
      }
    };
    
    return plans[tier] || plans.free;
  };

  return (
    <Container 
      maxWidth={false} 
      disableGutters
      sx={{ 
        pt: 2, 
        pb: 5, 
        px: 0,
        width: '100vw',
        maxWidth: '100vw',
        overflowX: 'hidden',
        background: theme.palette.mode === 'dark' 
          ? 'radial-gradient(circle at 50% 50%, rgba(0, 74, 173, 0.1) 0%, rgba(15, 37, 68, 0.05) 100%)'
          : 'radial-gradient(circle at 50% 50%, rgba(253, 253, 253, 0.9) 0%, rgba(203, 241, 153, 0.1) 100%)',
        minHeight: 'calc(100vh - 64px)',
      }}
    >
      {/* Geri Git Button */}
      <Box 
        sx={{ 
          position: 'absolute',
          top: { xs: '12px', sm: '16px' }, // Adjust top position for different screen sizes
          left: { xs: '26px', sm: '30px' }, // Moved 14px to the right
          zIndex: 10
        }}
      >
        <Button
          onClick={() => navigate('/workflows')} // Navigate directly to workflows
          variant="text"
          size="small"
          startIcon={<span>←</span>}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color: theme.palette.mode === 'dark' ? brandColors.accent : brandColors.primary,
            fontSize: '0.85rem',
            fontWeight: 500,
            padding: '4px 8px',
            minWidth: 'auto',
            borderRadius: '6px',
            backgroundColor: theme.palette.mode === 'dark' 
              ? 'rgba(74, 144, 226, 0.15)' 
              : 'rgba(203, 241, 153, 0.2)',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' 
                ? 'rgba(74, 144, 226, 0.25)' 
                : 'rgba(203, 241, 153, 0.4)',
            },
          }}
        >
          Geri Git
        </Button>
      </Box>
      
      {error && <Alert severity="error" sx={{ mb: 3, mx: { xs: 2, sm: 4, md: 6, lg: 8 } }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3, mx: { xs: 2, sm: 4, md: 6, lg: 8 } }} onClose={() => setSuccess('')}>{success}</Alert>}

      <PageHeader sx={{ mx: { xs: 2, sm: 4, md: 6, lg: 8 }, mt: { xs: 10, sm: 9, md: 6 } }}> {/* Further increased top margin */}
        <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center', px: { xs: 2, sm: 4, md: 6 } }}>
          <Typography 
            variant="h4" 
            component="h1" 
            sx={{ 
              fontWeight: 700, 
              mb: 1.5,
              color: theme.palette.mode === 'dark' ? '#fff' : brandColors.primary,
              letterSpacing: '-0.02em',
              fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' }
            }}
          >
            Abonelik Planları
          </Typography>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              maxWidth: '800px', 
              mx: 'auto',
              color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : brandColors.neutral,
              fontSize: { xs: '0.85rem', sm: '0.9rem' },
              lineHeight: 1.5
            }}
          >
            İhtiyaçlarınıza uygun planı seçin ve daha fazla özelliğe erişim kazanın.
            Tüm planlar istediğiniz zaman iptal edilebilir.
          </Typography>
        </Box>
      </PageHeader>

      <Grid container spacing={3} sx={{ mx: { xs: 0, sm: 2, md: 4, lg: 6 } }}>
        {/* Profile Section */}
        <Grid item xs={12} md={3} lg={2.5}>
          <Stack spacing={2.5} sx={{ mb: { xs: 4, md: 0 } }}> {/* Add bottom margin on small screens */}
            <SubscriptionCard elevation={0}>
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <Typography 
                  variant="h6" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 600, 
                    color: theme.palette.mode === 'dark' ? brandColors.accent : brandColors.primary,
                    fontSize: '1.1rem',
                    letterSpacing: '-0.01em'
                  }}
                >
                  Hesap Bilgileri
                </Typography>
                <Box sx={{ position: 'relative', display: 'inline-block', mb: 3 }}>
                  <ProfileAvatar src={profileData.avatarUrl} />
                </Box>

                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 1, 
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    color: theme.palette.mode === 'dark' ? '#fff' : '#000'
                  }}
                >
                  {profileData.name}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    mb: 3,
                    fontSize: '0.85rem',
                    opacity: 0.8
                  }}
                >
                  {session?.user?.email}
                </Typography>
                
                <Divider sx={{ 
                  my: 2,
                  borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'
                }} />
                
                <Box sx={{ textAlign: 'left', mt: 2 }}>
                  <Typography 
                    variant="subtitle2" 
                    gutterBottom 
                    sx={{ 
                      fontWeight: 600, 
                      color: theme.palette.mode === 'dark' ? brandColors.accent : brandColors.primary,
                      fontSize: '0.85rem',
                      letterSpacing: '0.02em',
                      textTransform: 'uppercase',
                      opacity: 0.9
                    }}
                  >
                    Mevcut Abonelik
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem', opacity: 0.85 }}>Plan:</Typography>
                    <Chip
                      label={getPlanDetails(currentTier).name}
                      size="small"
                      sx={{ 
                        backgroundColor: theme.palette.mode === 'dark' 
                          ? `rgba(74, 144, 226, 0.15)` 
                          : `rgba(26, 58, 107, 0.08)`,
                        color: theme.palette.mode === 'dark' ? brandColors.accent : brandColors.primary,
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        height: '22px'
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem', opacity: 0.85 }}>Durum:</Typography>
                    <Chip
                      label={isCancelling ? 'İptal Edilecek' : isActive ? 'Aktif' : 'Aktif Değil'}
                      color={isCancelling ? 'warning' : isActive ? 'success' : 'default'}
                      size="small"
                      sx={{ height: '22px', fontSize: '0.7rem' }}
                    />
                  </Box>
                  {subscriptionDetails?.subscription_end && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem', opacity: 0.85 }}>Bitiş Tarihi:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
                        {new Date(subscriptionDetails.subscription_end).toLocaleDateString('tr-TR')}
                      </Typography>
                    </Box>
                  )}
                </Box>
                
                <Box sx={{ mt: 3, mb: 2 }}> {/* Increased bottom margin */}
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/dashboard')}
                    fullWidth
                    sx={{
                      borderColor: theme.palette.mode === 'dark' ? brandColors.accent : brandColors.primary,
                      color: theme.palette.mode === 'dark' ? brandColors.accent : brandColors.primary,
                      borderRadius: '10px',
                      textTransform: 'none',
                      fontWeight: 500,
                      py: 1,
                      '&:hover': {
                        borderColor: theme.palette.mode === 'dark' ? brandColors.accent : brandColors.primary,
                        backgroundColor: theme.palette.mode === 'dark' 
                          ? `rgba(74, 144, 226, 0.1)` 
                          : `rgba(26, 58, 107, 0.05)`,
                      }
                    }}
                  >
                    Profili Düzenle
                  </Button>
                </Box>
              </CardContent>
            </SubscriptionCard>
            
            <SubscriptionCard elevation={0}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <StarIcon sx={{ 
                    color: theme.palette.mode === 'dark' ? brandColors.accent : brandColors.primary, 
                    mr: 1,
                    fontSize: '1.2rem'
                  }} />
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 600, 
                      color: theme.palette.mode === 'dark' ? brandColors.accent : brandColors.primary,
                      fontSize: '1.1rem'
                    }}
                  >
                    Kalan Krediler
                  </Typography>
                </Box>
                
                <Box sx={{
                  background: theme.palette.mode === 'dark' 
                    ? 'linear-gradient(135deg, rgba(74, 144, 226, 0.1) 0%, rgba(26, 58, 107, 0.1) 100%)'
                    : 'linear-gradient(135deg, rgba(245, 248, 252, 0.8) 0%, rgba(230, 240, 255, 0.8) 100%)',
                  borderRadius: '12px',
                  p: 2,
                  my: 2,
                  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(74, 144, 226, 0.15)' : 'rgba(26, 58, 107, 0.08)'}`,
                }}>
                  <Typography 
                    variant="h3" 
                    sx={{ 
                      fontWeight: 700, 
                      color: theme.palette.mode === 'dark' ? brandColors.accent : brandColors.primary,
                      textAlign: 'center',
                      fontSize: { xs: '2.5rem', sm: '3rem' },
                      letterSpacing: '-0.02em'
                    }}
                  >
                    {subscriptionDetails?.credits_remaining ?? 0}
                  </Typography>
                </Box>
                
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    mb: 2, 
                    textAlign: 'center',
                    fontSize: '0.85rem',
                    opacity: 0.9
                  }}
                >
                  Krediler, görsel oluşturma ve diğer premium özellikleri kullanmanızı sağlar.
                </Typography>
                
                {currentTier === 'free' && (
                  <Alert 
                    severity="info" 
                    sx={{ 
                      mt: 2,
                      borderRadius: '10px',
                      '& .MuiAlert-icon': {
                        color: theme.palette.mode === 'dark' ? brandColors.accent : brandColors.primary
                      }
                    }}
                  >
                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                      Daha fazla kredi için abonelik planınızı yükseltin.
                    </Typography>
                  </Alert>
                )}
              </CardContent>
            </SubscriptionCard>
            
            {/* Security Badge */}
            <SecurityBadge>
              <SecurityIcon sx={{ 
                color: theme.palette.mode === 'dark' ? brandColors.accent : brandColors.primary,
                fontSize: '1.1rem'
              }} />
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 500,
                  fontSize: '0.85rem',
                  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.75)'
                }}
              >
                Tüm ödemeler güvenli Stripe altyapısı ile işlenir
              </Typography>
            </SecurityBadge>
          </Stack>
        </Grid>

        {/* Subscription Plans Section */}
        <Grid item xs={12} md={9} lg={9.5}>
          {/* Remove duplicate heading */}

          <Grid container spacing={3} sx={{ mx: { xs: -1, sm: 0 } }}>
            {/* Temel Plan */}
            <Grid item xs={12} sm={6} lg={5} xl={4}>
              <PlanCard elevation={0} selected={currentTier === 'basic'}>
                <PlanBadge sx={{ 
                  background: theme.palette.mode === 'dark' 
                    ? 'linear-gradient(135deg, #004aad 0%, #A2C3A4 100%)' 
                    : 'linear-gradient(135deg, #004aad 0%, #cbf199 100%)'
                }}>
                  En Popüler
                </PlanBadge>
                <CardContent sx={{ p: 3.5 }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 700, 
                      color: theme.palette.mode === 'dark' ? '#fff' : brandColors.primary, 
                      mb: 1,
                      fontSize: '1.2rem',
                      letterSpacing: '-0.01em'
                    }}
                  >
                    Temel Paket
                  </Typography>
                  <Typography 
                    variant="subtitle2" 
                    color="text.secondary" 
                    sx={{ 
                      mb: 2,
                      fontSize: '0.85rem',
                      opacity: 0.85
                    }}
                  >
                    Bireysel mimarlar ve küçük ofisler için
                  </Typography>
                  
                  <Box sx={{
                    background: theme.palette.mode === 'dark' 
                      ? 'linear-gradient(135deg, rgba(0, 74, 173, 0.1) 0%, rgba(26, 58, 107, 0.1) 100%)'
                      : 'linear-gradient(135deg, rgba(253, 253, 253, 0.8) 0%, rgba(203, 241, 153, 0.2) 100%)',
                    borderRadius: '12px',
                    p: 2,
                    mb: 3,
                    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(0, 74, 173, 0.15)' : 'rgba(162, 195, 164, 0.15)'}`,
                    textAlign: 'center'
                  }}>
                    <Typography 
                      variant="h4" 
                      sx={{ 
                        fontWeight: 700, 
                        mb: 0.5,
                        fontSize: { xs: '1.8rem', sm: '2rem' },
                        color: theme.palette.mode === 'dark' ? '#fff' : brandColors.primary,
                        letterSpacing: '-0.02em'
                      }}
                    >
                      159 ₺
                      <Typography 
                        component="span" 
                        variant="body2" 
                        sx={{ 
                          verticalAlign: 'middle',
                          fontSize: '0.9rem',
                          opacity: 0.8,
                          ml: 0.5
                        }}
                      >
                        /ay
                      </Typography>
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 600, 
                        color: theme.palette.mode === 'dark' ? brandColors.accent : brandColors.secondary,
                        fontSize: '0.9rem'
                      }}
                    >
                      500 Render Kredisi/Ay
                    </Typography>
                  </Box>
                  
                  <Divider sx={{ 
                    my: 2.5,
                    borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'
                  }} />
                  
                  <FeatureList sx={{ maxHeight: '240px', overflowY: 'auto', pr: 1, my: 2.5 }}>
                    {commonFeatures.map((feature, index) => (
                      <li key={index}>
                        <FeatureIcon />
                        {feature}
                      </li>
                    ))}
                  </FeatureList>
                  
                  <Button
                    variant={currentTier === 'basic' ? 'outlined' : 'contained'}
                    fullWidth
                    onClick={() => handleUpgrade('basic')}
                    disabled={checkoutLoading === 'basic' || loading || currentTier === 'basic'}
                    sx={{ 
                      mt: 2,
                      backgroundColor: currentTier !== 'basic' 
                        ? theme.palette.mode === 'dark' ? brandColors.accent : brandColors.primary
                        : 'transparent',
                      borderColor: theme.palette.mode === 'dark' ? brandColors.accent : brandColors.primary,
                      color: currentTier !== 'basic' 
                        ? '#fff' 
                        : theme.palette.mode === 'dark' ? brandColors.accent : brandColors.primary,
                      borderRadius: '10px',
                      py: 1.2,
                      textTransform: 'none',
                      fontWeight: 600,
                      letterSpacing: '0.01em',
                      boxShadow: currentTier !== 'basic' && !theme.palette.mode === 'dark'
                        ? '0 4px 12px rgba(26, 58, 107, 0.15)'
                        : 'none',
                      '&:hover': {
                        backgroundColor: currentTier !== 'basic' 
                          ? theme.palette.mode === 'dark' ? brandColors.accent : brandColors.primary
                          : theme.palette.mode === 'dark' ? 'rgba(74, 144, 226, 0.1)' : 'rgba(26, 58, 107, 0.05)',
                        borderColor: theme.palette.mode === 'dark' ? brandColors.accent : brandColors.primary,
                        boxShadow: currentTier !== 'basic' && !theme.palette.mode === 'dark'
                          ? '0 6px 16px rgba(26, 58, 107, 0.2)'
                          : 'none',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: currentTier === 'basic' 
                          ? 'transparent'
                          : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                        color: currentTier === 'basic' 
                          ? theme.palette.mode === 'dark' ? 'rgba(74, 144, 226, 0.6)' : 'rgba(26, 58, 107, 0.6)'
                          : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                        borderColor: currentTier === 'basic'
                          ? theme.palette.mode === 'dark' ? 'rgba(74, 144, 226, 0.3)' : 'rgba(26, 58, 107, 0.3)'
                          : 'transparent',
                      }
                    }}
                  >
                    {checkoutLoading === 'basic' ? (
                      <CircularProgress size={22} color="inherit"/>
                    ) : currentTier === 'basic' ? (
                      'Mevcut Plan'
                    ) : (
                      'Yükselt'
                    )}
                  </Button>
                </CardContent>
              </PlanCard>
            </Grid>
            
            {/* Profesyonel Plan */}
            <Grid item xs={12} sm={6} lg={5} xl={4}>
              <PlanCard elevation={0} selected={currentTier === 'pro'}>
                <CardContent sx={{ p: 3.5 }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 700, 
                      color: theme.palette.mode === 'dark' ? '#fff' : brandColors.primary, 
                      mb: 1,
                      fontSize: '1.2rem',
                      letterSpacing: '-0.01em'
                    }}
                  >
                    Profesyonel
                  </Typography>
                  <Typography 
                    variant="subtitle2" 
                    color="text.secondary" 
                    sx={{ 
                      mb: 2,
                      fontSize: '0.85rem',
                      opacity: 0.85
                    }}
                  >
                    Profesyonel mimarlık ofisleri için tam donanımlı çözüm
                  </Typography>
                  
                  <Box sx={{
                    background: theme.palette.mode === 'dark' 
                      ? 'linear-gradient(135deg, rgba(0, 74, 173, 0.15) 0%, rgba(26, 58, 107, 0.15) 100%)'
                      : 'linear-gradient(135deg, rgba(253, 253, 253, 0.9) 0%, rgba(203, 241, 153, 0.3) 100%)',
                    borderRadius: '12px',
                    p: 2,
                    mb: 3,
                    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(0, 74, 173, 0.2)' : 'rgba(162, 195, 164, 0.2)'}`,
                    textAlign: 'center'
                  }}>
                    <Typography 
                      variant="h4" 
                      sx={{ 
                        fontWeight: 700, 
                        mb: 0.5,
                        fontSize: { xs: '1.8rem', sm: '2rem' },
                        color: theme.palette.mode === 'dark' ? '#fff' : brandColors.primary,
                        letterSpacing: '-0.02em'
                      }}
                    >
                      599 ₺
                      <Typography 
                        component="span" 
                        variant="body2" 
                        sx={{ 
                          verticalAlign: 'middle',
                          fontSize: '0.9rem',
                          opacity: 0.8,
                          ml: 0.5
                        }}
                      >
                        /ay
                      </Typography>
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 600, 
                        color: theme.palette.mode === 'dark' ? brandColors.accent : brandColors.secondary,
                        fontSize: '0.9rem'
                      }}
                    >
                      2000 Render Kredisi/Ay
                    </Typography>
                  </Box>
                  
                  <Divider sx={{ 
                    my: 2.5,
                    borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'
                  }} />
                  
                  <FeatureList sx={{ maxHeight: '240px', overflowY: 'auto', pr: 1, my: 2.5 }}>
                    {[...commonFeatures, ...proExclusiveFeatures].map((feature, index) => (
                      <li key={index}>
                        <FeatureIcon />
                        {feature}
                      </li>
                    ))}
                  </FeatureList>
                  
                  <Button
                    variant={currentTier === 'pro' ? 'outlined' : 'contained'}
                    fullWidth
                    onClick={() => handleUpgrade('pro')}
                    disabled={checkoutLoading === 'pro' || loading || currentTier === 'pro'}
                    sx={{ 
                      mt: 2,
                      backgroundColor: currentTier !== 'pro' 
                        ? theme.palette.mode === 'dark' ? brandColors.accent : brandColors.primary
                        : 'transparent',
                      borderColor: theme.palette.mode === 'dark' ? brandColors.accent : brandColors.primary,
                      color: currentTier !== 'pro' 
                        ? '#fff' 
                        : theme.palette.mode === 'dark' ? brandColors.accent : brandColors.primary,
                      borderRadius: '10px',
                      py: 1.2,
                      textTransform: 'none',
                      fontWeight: 600,
                      letterSpacing: '0.01em',
                      boxShadow: currentTier !== 'pro' && !theme.palette.mode === 'dark'
                        ? '0 4px 12px rgba(26, 58, 107, 0.15)'
                        : 'none',
                      '&:hover': {
                        backgroundColor: currentTier !== 'pro' 
                          ? theme.palette.mode === 'dark' ? brandColors.accent : brandColors.primary
                          : theme.palette.mode === 'dark' ? 'rgba(74, 144, 226, 0.1)' : 'rgba(26, 58, 107, 0.05)',
                        borderColor: theme.palette.mode === 'dark' ? brandColors.accent : brandColors.primary,
                        boxShadow: currentTier !== 'pro' && !theme.palette.mode === 'dark'
                          ? '0 6px 16px rgba(26, 58, 107, 0.2)'
                          : 'none',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: currentTier === 'pro' 
                          ? 'transparent'
                          : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                        color: currentTier === 'pro' 
                          ? theme.palette.mode === 'dark' ? 'rgba(74, 144, 226, 0.6)' : 'rgba(26, 58, 107, 0.6)'
                          : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                        borderColor: currentTier === 'pro'
                          ? theme.palette.mode === 'dark' ? 'rgba(74, 144, 226, 0.3)' : 'rgba(26, 58, 107, 0.3)'
                          : 'transparent',
                      }
                    }}
                  >
                    {checkoutLoading === 'pro' ? (
                      <CircularProgress size={22} color="inherit"/>
                    ) : currentTier === 'pro' ? (
                      'Mevcut Plan'
                    ) : (
                      'Yükselt'
                    )}
                  </Button>
                </CardContent>
              </PlanCard>
            </Grid>
          </Grid>
          
          {/* Subscription Management */}
          {currentTier !== 'free' && (
            <Paper 
              elevation={0} 
              sx={{ 
                mt: 5, 
                p: 3.5, 
                borderRadius: '16px',
                backgroundColor: theme.palette.mode === 'dark' 
                  ? 'rgba(15, 37, 68, 0.6)' 
                  : 'rgba(245, 248, 252, 0.8)',
                maxWidth: { lg: '85%', xl: '75%' },
                backdropFilter: 'blur(10px)',
                border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(74, 144, 226, 0.1)' : 'rgba(26, 58, 107, 0.06)'}`,
              }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600, 
                  color: theme.palette.mode === 'dark' ? '#fff' : brandColors.primary, 
                  mb: 2.5,
                  fontSize: '1.1rem',
                  letterSpacing: '-0.01em'
                }}
              >
                Abonelik Yönetimi
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      mb: 1,
                      fontSize: '0.9rem',
                      lineHeight: 1.6,
                      color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.75)'
                    }}
                  >
                    {isCancelling ? (
                      <>
                        Aboneliğiniz dönem sonunda ({new Date(subscriptionDetails.subscription_end).toLocaleDateString('tr-TR')}) iptal edilecektir.
                        Bu tarihe kadar tüm özelliklere erişiminiz devam edecektir.
                      </>
                    ) : (
                      <>
                        Aboneliğinizi istediğiniz zaman iptal edebilir veya Stripe güvenli ödeme portalı üzerinden fatura bilgilerinizi yönetebilirsiniz.
                        İptal ettiğinizde, mevcut dönemin sonuna kadar tüm özelliklere erişiminiz devam eder.
                      </>
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Stack direction="column" spacing={1}>
                    {isActive && !isCancelling && (
                      <>
                        <Button
                          variant="outlined"
                          onClick={handleManageBilling}
                          disabled={loading}
                          startIcon={<CreditCardIcon sx={{ fontSize: '1.1rem' }} />}
                          sx={{
                            borderColor: theme.palette.mode === 'dark' ? brandColors.accent : brandColors.primary,
                            color: theme.palette.mode === 'dark' ? brandColors.accent : brandColors.primary,
                            borderRadius: '10px',
                            textTransform: 'none',
                            fontWeight: 500,
                            py: 1,
                            '&:hover': {
                              borderColor: theme.palette.mode === 'dark' ? brandColors.accent : brandColors.primary,
                              backgroundColor: theme.palette.mode === 'dark' 
                                ? `rgba(74, 144, 226, 0.1)` 
                                : `rgba(26, 58, 107, 0.05)`,
                            }
                          }}
                        >
                          Faturayı Yönet
                        </Button>
                        
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={handleCancel}
                          disabled={cancelling || loading}
                          sx={{
                            borderRadius: '10px',
                            textTransform: 'none',
                            fontWeight: 500,
                            py: 1,
                          }}
                        >
                          {cancelling ? <CircularProgress size={20} color="inherit"/> : 'Aboneliği İptal Et'}
                        </Button>
                      </>
                    )}
                  </Stack>
                </Grid>
              </Grid>
              
              <StripeBadge>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                  Ödemeler Stripe tarafından güvenle işlenir
                </Typography>
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/b/ba/Stripe_Logo%2C_revised_2016.svg" 
                  alt="Stripe" 
                />
              </StripeBadge>
            </Paper>
          )}
          
          {/* Guarantees */}
          <Paper 
            elevation={0} 
            sx={{ 
              mt: 5, 
              p: 3.5, 
              borderRadius: '16px',
              backgroundColor: theme.palette.mode === 'dark' 
                ? 'rgba(15, 37, 68, 0.6)' 
                : 'rgba(255, 255, 255, 0.9)',
              maxWidth: { lg: '85%', xl: '75%' },
              backdropFilter: 'blur(10px)',
              border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(74, 144, 226, 0.1)' : 'rgba(26, 58, 107, 0.06)'}`,
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600, 
                color: theme.palette.mode === 'dark' ? '#fff' : brandColors.primary, 
                mb: 3,
                fontSize: '1.1rem',
                letterSpacing: '-0.01em'
              }}
            >
              Güvencelerimiz
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Box sx={{ 
                  textAlign: 'center',
                  p: 2,
                  borderRadius: '12px',
                  height: '100%',
                  background: theme.palette.mode === 'dark' 
                    ? 'rgba(15, 37, 68, 0.3)' 
                    : 'rgba(245, 248, 252, 0.7)',
                  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(74, 144, 226, 0.1)' : 'rgba(26, 58, 107, 0.05)'}`,
                }}>
                  <SecurityIcon sx={{ 
                    fontSize: 36, 
                    color: theme.palette.mode === 'dark' ? brandColors.accent : brandColors.primary, 
                    mb: 1.5,
                    opacity: 0.9
                  }} />
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      fontWeight: 600, 
                      mb: 1,
                      color: theme.palette.mode === 'dark' ? '#fff' : brandColors.primary,
                      fontSize: '0.95rem'
                    }}
                  >
                    Güvenli Ödeme
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ fontSize: '0.85rem', lineHeight: 1.5 }}
                  >
                    Tüm ödemeler Stripe güvenli altyapısı ile işlenir ve bilgileriniz korunur.
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <Box sx={{ 
                  textAlign: 'center',
                  p: 2,
                  borderRadius: '12px',
                  height: '100%',
                  background: theme.palette.mode === 'dark' 
                    ? 'rgba(15, 37, 68, 0.3)' 
                    : 'rgba(245, 248, 252, 0.7)',
                  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(74, 144, 226, 0.1)' : 'rgba(26, 58, 107, 0.05)'}`,
                }}>
                  <CheckCircleIcon sx={{ 
                    fontSize: 36, 
                    color: theme.palette.mode === 'dark' ? brandColors.accent : brandColors.primary, 
                    mb: 1.5,
                    opacity: 0.9
                  }} />
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      fontWeight: 600, 
                      mb: 1,
                      color: theme.palette.mode === 'dark' ? '#fff' : brandColors.primary,
                      fontSize: '0.95rem'
                    }}
                  >
                    İstediğiniz Zaman İptal
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ fontSize: '0.85rem', lineHeight: 1.5 }}
                  >
                    Aboneliğinizi dilediğiniz zaman iptal edebilirsiniz. Gizli ücretler yoktur.
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <Box sx={{ 
                  textAlign: 'center',
                  p: 2,
                  borderRadius: '12px',
                  height: '100%',
                  background: theme.palette.mode === 'dark' 
                    ? 'rgba(15, 37, 68, 0.3)' 
                    : 'rgba(245, 248, 252, 0.7)',
                  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(74, 144, 226, 0.1)' : 'rgba(26, 58, 107, 0.05)'}`,
                }}>
                  <CreditCardIcon sx={{ 
                    fontSize: 36, 
                    color: theme.palette.mode === 'dark' ? brandColors.accent : brandColors.primary, 
                    mb: 1.5,
                    opacity: 0.9
                  }} />
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      fontWeight: 600, 
                      mb: 1,
                      color: theme.palette.mode === 'dark' ? '#fff' : brandColors.primary,
                      fontSize: '0.95rem'
                    }}
                  >
                    Şeffaf Fiyatlandırma
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ fontSize: '0.85rem', lineHeight: 1.5 }}
                  >
                    Tüm fiyatlar KDV dahildir ve gizli ücretler yoktur. Ne gördüğünüz odur.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Subscription;
